// Map layer types
export type MapLayerType =
  | 'incidents'
  | 'damage_assessments'
  | 'road_closures'
  | 'utility_outages'
  | 'critical_infrastructure'
  | 'supply_requests'
  | 'deployed_resources'
  | 'tasks'
  | 'shelters'
  | 'weather_alerts'
  | 'tropical_wsp_34kt'
  | 'tropical_wsp_64kt'
  | 'hazmat_releases'
  | 'plume_contours'
  | 'drones'
  | 'flight_missions'
  | 'flight_snapshots'
  | 'fire_perimeters'
  | 'personnel'
  | 'evacuation_zones'
  | 'equipment'
  | 'fire_predictions'
  | 'smoke_plumes'
  | 'thermal_hotspots'
  | 'search_patterns'
  | 'community_reports'
  | 'orthophotos'
  | 'flood_inundation_analysis'
  | 'flood_inundation_rfc_forecast'
  | 'flood_inundation_nwm_forecast'
  | 'storm_surge_extents'
  | 'predicted_road_closures'
  | 'predicted_damage_assessments'
  | 'population_blocks'
  | 'svi_tracts'
  // W13-DEBRIS: FEMA Category A debris estimates (incident-dependent).
  | 'debris_estimates';

export const ALL_MAP_LAYERS: MapLayerType[] = [
  'incidents',
  'damage_assessments',
  'road_closures',
  'utility_outages',
  'critical_infrastructure',
  'supply_requests',
  'deployed_resources',
  'tasks',
  'shelters',
  'weather_alerts',
  'tropical_wsp_34kt',
  'tropical_wsp_64kt',
  'hazmat_releases',
  'plume_contours',
  'drones',
  'flight_missions',
  'flight_snapshots',
  'fire_perimeters',
  'personnel',
  'evacuation_zones',
  'equipment',
  'fire_predictions',
  'smoke_plumes',
  'thermal_hotspots',
  'search_patterns',
  'community_reports',
  'orthophotos',
  'flood_inundation_analysis',
  'flood_inundation_rfc_forecast',
  'flood_inundation_nwm_forecast',
  'storm_surge_extents',
  'predicted_road_closures',
  'predicted_damage_assessments',
  'population_blocks',
  'svi_tracts',
  'debris_estimates',
];

/**
 * Layers rendered by their own dedicated choropleth FeatureLayers
 * (useChoroplethLayers), NOT by the generic operational points/lines/polygons
 * layers.
 *
 * The operational partition takes every polygon feature, so these were being
 * drawn twice — once with the class-break choropleth renderer and once as a
 * plain operational polygon on top. Besides the wasted draw, the operational
 * copy won every hitTest, so clicking a census block group opened the generic
 * feature popup ("Detailed view not yet available for population
 * (block-groups)") instead of the census popup, and the choropleth layer under
 * it was never reached.
 */
export const CHOROPLETH_LAYER_TYPES: ReadonlySet<MapLayerType> = new Set([
  'population_blocks',
  'svi_tracts',
]);

// Layer group configuration
export type LayerGroupId = 'incidents' | 'infrastructure' | 'operations' | 'weather' | 'hazmat' | 'wildfire' | 'aviation' | 'drone_imagery' | 'field_ops' | 'demographics';

export interface LayerGroup {
  id: LayerGroupId;
  label: string;
  color: string;
  layers: MapLayerType[];
  defaultExpanded: boolean;
}

/**
 * Modeled flood-inundation extent layers (our own /api/map/features products).
 * These are pulled OUT of the operational "weather" group and rendered together
 * with the NWS CatFIM reference layer in the single consolidated "Flood
 * Inundation" section of the layer menu (see LayerControl.tsx). Kept here as a
 * named constant so the menu section and any other consumer share one source of
 * truth. The layer types, DEFAULT_LAYER_CONFIG entries, and rendering paths are
 * unchanged — only the menu grouping moved.
 */
export const FLOOD_INUNDATION_EXTENT_LAYERS: MapLayerType[] = [
  'flood_inundation_analysis',
  'flood_inundation_rfc_forecast',
  'flood_inundation_nwm_forecast',
];

export const LAYER_GROUPS: LayerGroup[] = [
  {
    id: 'weather',
    label: 'Weather',
    color: '#0ea5e9', // sky blue
    // Flood-inundation extents moved to the consolidated "Flood Inundation"
    // section. Storm surge + predicted impacts stay here per product decision.
    layers: [
      'weather_alerts',
      'tropical_wsp_34kt',
      'tropical_wsp_64kt',
      'storm_surge_extents',
      'predicted_road_closures',
      'predicted_damage_assessments',
    ],
    defaultExpanded: true,
  },
  {
    id: 'incidents',
    label: 'Incidents & Response',
    color: '#f97316', // orange
    layers: ['incidents', 'damage_assessments', 'debris_estimates', 'tasks', 'community_reports'],
    defaultExpanded: true,
  },
  {
    id: 'hazmat',
    label: 'HAZMAT',
    color: '#7c3aed', // purple/violet
    layers: ['hazmat_releases', 'plume_contours'],
    defaultExpanded: true,
  },
  {
    id: 'infrastructure',
    label: 'Infrastructure',
    color: '#8b5cf6', // purple
    layers: ['road_closures', 'utility_outages', 'critical_infrastructure'],
    defaultExpanded: true,
  },
  {
    id: 'operations',
    label: 'Logistics & Resources',
    color: '#3b82f6', // blue
    layers: ['supply_requests', 'deployed_resources', 'shelters'],
    defaultExpanded: false,
  },
  {
    id: 'wildfire',
    label: 'Wildfire',
    color: '#ea580c', // deep orange
    layers: ['fire_perimeters', 'equipment', 'fire_predictions', 'thermal_hotspots', 'smoke_plumes'],
    defaultExpanded: true,
  },
  {
    id: 'aviation',
    label: 'Aviation & UAS',
    color: '#6366f1', // indigo
    layers: ['drones', 'flight_missions', 'flight_snapshots'],
    defaultExpanded: false,
  },
  {
    id: 'drone_imagery',
    label: 'Drone Imagery',
    color: '#8b5cf6', // violet
    layers: ['orthophotos'],
    defaultExpanded: false,
  },
  {
    id: 'field_ops',
    label: 'Field Operations',
    color: '#14b8a6', // teal
    layers: ['personnel', 'evacuation_zones', 'search_patterns'],
    defaultExpanded: false,
  },
  {
    id: 'demographics',
    label: 'Demographics',
    color: '#6366f1', // indigo
    layers: ['population_blocks', 'svi_tracts'],
    defaultExpanded: false,
  },
];

// Layer display configuration
export interface LayerConfig {
  visible: boolean;
  color: string;
  icon: string;
  label: string;
}

export const DEFAULT_LAYER_CONFIG: Record<MapLayerType, LayerConfig> = {
  weather_alerts: {
    visible: true,
    color: '#0ea5e9', // sky blue
    icon: 'weather',
    label: 'Weather Alerts',
  },
  incidents: {
    visible: true,
    color: '#f97316', // orange
    icon: 'incident',
    label: 'Incidents',
  },
  damage_assessments: {
    visible: true,
    color: '#ef4444', // red
    icon: 'damage',
    label: 'Damage Assessments',
  },
  debris_estimates: {
    visible: true,
    color: '#a16207', // amber-brown: debris (FEMA Category A)
    icon: 'debris',
    label: 'Debris Estimates',
  },
  road_closures: {
    visible: true,
    color: '#dc2626', // dark red
    icon: 'road',
    label: 'Road Closures',
  },
  utility_outages: {
    visible: true,
    color: '#eab308', // yellow
    icon: 'utility',
    label: 'Utility Outages',
  },
  critical_infrastructure: {
    visible: true,
    color: '#8b5cf6', // purple
    icon: 'infrastructure',
    label: 'Critical Infrastructure',
  },
  supply_requests: {
    visible: false,
    color: '#3b82f6', // blue
    icon: 'supply',
    label: 'Supply Requests',
  },
  deployed_resources: {
    visible: false,
    color: '#f59e0b', // amber
    icon: 'truck-ramp-box',
    label: 'Deployed Resources',
  },
  tasks: {
    visible: false,
    color: '#10b981', // green
    icon: 'task',
    label: 'Tasks',
  },
  shelters: {
    visible: false,
    color: '#06b6d4', // cyan
    icon: 'shelter',
    label: 'Shelters',
  },
  hazmat_releases: {
    visible: true,
    color: '#7c3aed', // purple/violet
    icon: 'hazmat',
    label: 'HAZMAT Releases',
  },
  plume_contours: {
    visible: true,
    color: '#dc2626', // red
    icon: 'plume',
    label: 'Plume Contours',
  },
  // Wildfire layers
  fire_perimeters: {
    visible: false,
    color: '#dc2626', // red - active fire
    icon: 'fire',
    label: 'Fire Perimeters',
  },
  equipment: {
    visible: false,
    color: '#f97316', // orange
    icon: 'fire_resource',
    label: 'Equipment',
  },
  fire_predictions: {
    visible: false,
    color: '#ea580c', // deep orange
    icon: 'fire_prediction',
    label: 'Fire Predictions',
  },
  thermal_hotspots: {
    visible: false,
    color: '#ef4444', // bright red - heat
    icon: 'thermal',
    label: 'Thermal Hotspots',
  },
  smoke_plumes: {
    visible: false,
    color: '#6b7280', // gray - smoke
    icon: 'smoke',
    label: 'Smoke Plumes',
  },
  // Aviation/UAS layers
  drones: {
    visible: false,
    color: '#6366f1', // indigo - aviation
    icon: 'drone',
    label: 'Drones / UAS',
  },
  flight_missions: {
    visible: false,
    color: '#4f46e5', // darker indigo
    icon: 'flight_mission',
    label: 'Flight Missions',
  },
  flight_snapshots: {
    visible: false,
    color: '#818cf8', // light indigo - imagery
    icon: 'flight_snapshot',
    label: 'Flight Snapshots',
  },
  // Field operations layers
  personnel: {
    visible: false,
    color: '#14b8a6', // teal
    icon: 'personnel',
    label: 'Personnel',
  },
  evacuation_zones: {
    visible: false,
    color: '#f59e0b', // amber - warning
    icon: 'evacuation',
    label: 'Evacuation Zones',
  },
  search_patterns: {
    visible: false,
    color: '#0ea5e9', // sky blue - search
    icon: 'search',
    label: 'Search Patterns',
  },
  community_reports: {
    visible: true,
    color: '#8b5cf6', // violet
    icon: 'users',
    label: 'Community Reports',
  },
  orthophotos: {
    visible: false,
    color: '#8b5cf6', // violet
    icon: 'map',
    label: 'Orthophotos',
  },
  flood_inundation_analysis: {
    visible: false,
    color: '#1d4ed8', // dark blue - currently observed
    icon: 'water',
    label: 'Flood — Now (NWM Analysis)',
  },
  flood_inundation_rfc_forecast: {
    visible: false,
    color: '#7c3aed', // purple - RFC forecast
    icon: 'water-arrow-up',
    label: 'Flood — RFC 5-Day Forecast',
  },
  flood_inundation_nwm_forecast: {
    visible: false,
    color: '#0ea5e9', // sky blue - NWM forecast
    icon: 'cloud-showers-water',
    label: 'Flood — NWM 5-Day Forecast',
  },
  tropical_wsp_34kt: {
    visible: false,
    color: '#7c3aed', // violet - NHC wind probability
    icon: 'wind',
    label: 'Tropical-storm-force wind chance (39+ mph)',
  },
  tropical_wsp_64kt: {
    visible: false,
    color: '#7c3aed', // violet - NHC wind probability
    icon: 'wind',
    label: 'Hurricane-force wind chance (74+ mph)',
  },
  storm_surge_extents: {
    visible: false,
    color: '#a855f7', // purple - surge
    icon: 'wave-square',
    label: 'Storm Surge',
  },
  predicted_road_closures: {
    visible: false,
    color: '#f97316', // orange - dashed predicted
    icon: 'road-circle-xmark',
    label: 'Predicted Road Closures',
  },
  predicted_damage_assessments: {
    visible: false,
    color: '#ef4444', // red
    icon: 'house-damage',
    label: 'Predicted Damage',
  },
  // Demographic choropleth layers
  population_blocks: {
    visible: false,
    color: '#6366f1', // indigo
    icon: 'people-group',
    label: 'Population (block-groups)',
  },
  svi_tracts: {
    visible: false,
    color: '#a855f7', // purple
    icon: 'shield-halved',
    label: 'Social vulnerability (SVI)',
  },
};

// Geographic bounding box
export interface BoundingBox {
  west: number;
  south: number;
  east: number;
  north: number;
}

// Map feature properties
export interface MapFeatureProperties {
  id: string;
  layer: MapLayerType;
  name: string;
  status: string;
  severity?: string;
  priority?: string;
  category?: string;
  utility_type?: string;
  event_type?: string; // For weather alerts
  urgency?: string; // For weather alerts
  expires_at?: string; // For weather alerts
  incident_id?: string;
  created_at: string;
  address?: string;
  description?: string;
  /**
   * Lifecycle state for predicted flood layers (predicted_road_closures,
   * predicted_damage_assessments): 'impacted' = currently flooded (red),
   * 'warning' = forecast/not yet impacted (amber). Null for other layers.
   */
  lifecycle?: string;
}

// GeoJSON Feature
export interface MapFeature {
  type: 'Feature';
  geometry: GeoJSON.Geometry | null;
  properties: MapFeatureProperties;
}

// GeoJSON FeatureCollection response
export interface MapFeatureCollection {
  type: 'FeatureCollection';
  features: MapFeature[];
  metadata: {
    total_by_layer: Record<MapLayerType, number>;
    total: number;
    bbox: BoundingBox | null;
    fetched_at: string;
    /**
     * True when the backend hit its row limit. `total` and `total_by_layer` are
     * computed from post-limit rows, so entire layers may be missing from the
     * response. The UI must say so rather than presenting a partial picture as
     * complete.
     */
    truncated?: boolean;
  };
}

// Query filters
export interface MapFilters {
  incidentId?: string;
  status?: string[];
  dateFrom?: string;
  dateTo?: string;
  search?: string;
  changedSince?: string; // ISO date string for "changed since" filter
}

// Geocode result
export interface GeocodeResult {
  lat: number;
  lng: number;
  display_name: string;
  address_components: {
    house_number?: string;
    road?: string;
    city?: string;
    state?: string;
    zip_code?: string;
    county?: string;
    country?: string;
  };
}

// Geocode response
export interface GeocodeResponse {
  results: GeocodeResult[];
  query: string;
}

// Layer counts response
export interface LayerCountsResponse {
  counts: Record<MapLayerType, number>;
  total: number;
  incident_id?: string;
}

// Viewport state
export interface ViewportState {
  center: [number, number]; // [lng, lat]
  zoom: number;
  bounds?: BoundingBox;
}
